import { useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Building2, FileText, TrendingDown, TrendingUp, Wallet } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { FinancialEntry, ProjectDetail, ProjectIssuedInvoice } from '../../lib/projectsApi';
import { EditWithAIButton } from '../ui/EditWithAIButton';
import { IssuedInvoiceStatusBadge } from '../domain/issuedInvoices/IssuedInvoiceStatusBadge';
import { useI18n } from '../../contexts/I18nContext';
import { CHART_TOOLTIP_ITEM_STYLE, CHART_TOOLTIP_STYLE } from '../../lib/chartTheme';

// ---------------------------------------------------------------------------
// Project → Financials tab (CLE-72, REDESIGN 1)
//
// Simplifies the old P&L overview into compact summary cards and adds three
// charts driven by the project's financial entries:
//   • income vs. expenses per month (grouped bar chart)
//   • expense breakdown by category (pie chart)
//   • cumulative profit / loss trend (line chart)
//
// The entry table mirrors the Invoices/Transactions table style so the two
// pages look consistent (same border/zebra/row-highlight treatment).
// ---------------------------------------------------------------------------

const MONTH_LABELS = [
  'Ian',
  'Feb',
  'Mar',
  'Apr',
  'Mai',
  'Iun',
  'Iul',
  'Aug',
  'Sep',
  'Oct',
  'Noi',
  'Dec',
];

// Brand-aligned palette for the pie slices.
const PIE_COLORS = [
  '#6366f1',
  '#8b5cf6',
  '#ec4899',
  '#f59e0b',
  '#10b981',
  '#06b6d4',
  '#ef4444',
  '#84cc16',
  '#f97316',
  '#14b8a6',
];

function formatMoney(value: number, currency = 'RON'): string {
  return `${value.toLocaleString('ro-RO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency}`;
}

function formatCompact(value: number): string {
  return value.toLocaleString('ro-RO', {
    notation: 'compact',
    maximumFractionDigits: 1,
  });
}

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString('ro-RO') : '—';
}

// --- Summary card ----------------------------------------------------------

function SummaryCard({
  label,
  value,
  hint,
  tone,
  icon,
}: {
  label: string;
  value: string;
  hint?: string;
  tone: 'neutral' | 'positive' | 'negative';
  icon: React.ReactNode;
}) {
  const toneClass =
    tone === 'positive' ? 'text-green-600' : tone === 'negative' ? 'text-red-600' : 'text-gray-900';
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
        <span className="text-gray-400">{icon}</span>
      </div>
      <p className={`mt-2 text-2xl font-semibold ${toneClass}`}>{value}</p>
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

// --- Chart card wrapper ----------------------------------------------------

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      <div className="mt-4 h-64 w-full">{children}</div>
    </div>
  );
}

function EmptyChart({ message }: { message: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-gray-400">{message}</div>
  );
}

// --- Facturi emise for the project, across firms ----------------------------
//
// The project is the unit of work; the firm is whoever invoiced it. Every
// Firmio-issued invoice tagged with the project is listed with its firm, and
// the ones ANAF holds (SENT/ACCEPTED, stornos negative) are summed per firm.

function IssuedInvoicesSection({
  invoices,
  byCompany,
  totalInvoiced,
}: {
  invoices: ProjectIssuedInvoice[];
  byCompany: { company: { id: string; name: string }; count: number; totalAmount: number }[];
  totalInvoiced: number;
}) {
  const { t } = useI18n();
  const currency = invoices[0]?.currency ?? 'RON';
  const multiFirm = byCompany.length > 1;

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-900">{t('projects.fin.issued.title')}</h3>
          <span className="text-xs text-gray-500">
            {invoices.length}{' '}
            {invoices.length === 1
              ? t('projects.fin.issued.invoice')
              : t('projects.fin.issued.invoices')}
          </span>
        </div>
        <div className="text-sm text-gray-700">
          {t('projects.fin.issued.total')}{' '}
          <span className="font-semibold text-gray-900">
            {formatMoney(totalInvoiced, currency)}
          </span>
        </div>
      </div>

      {multiFirm && (
        <div className="flex flex-wrap gap-2 border-b border-gray-100 px-4 py-3">
          {byCompany.map((b) => (
            <span
              key={b.company.id}
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-700"
            >
              <Building2 size={12} className="text-gray-400" />
              <span className="font-medium">{b.company.name}</span>
              <span className="text-gray-500">
                · {formatMoney(b.totalAmount, currency)} · {b.count}{' '}
                {b.count === 1
                  ? t('projects.fin.issued.invoice')
                  : t('projects.fin.issued.invoices')}
              </span>
            </span>
          ))}
        </div>
      )}

      {invoices.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-gray-500">
          {t('projects.fin.issued.empty')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left font-medium">
                  {t('projects.fin.issued.col.number')}
                </th>
                <th className="px-3 py-2 text-left font-medium">{t('projects.fin.col.date')}</th>
                <th className="px-3 py-2 text-left font-medium">
                  {t('projects.fin.issued.col.customer')}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {t('projects.fin.issued.col.company')}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {t('projects.fin.issued.col.status')}
                </th>
                <th className="px-3 py-2 text-right font-medium">{t('projects.fin.col.amount')}</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv, i) => (
                <tr
                  key={inv.id}
                  className={`border-t border-gray-100 hover:bg-gray-50 ${i % 2 === 1 ? 'bg-gray-50/40' : ''}`}
                >
                  <td className="px-3 py-2">
                    <Link
                      to={`/financials/issued/${inv.id}`}
                      className="font-medium text-brand-700 hover:underline"
                    >
                      {inv.fullNumber ?? t('projects.fin.issued.draft')}
                    </Link>
                    {inv.stornoOfId && (
                      <span className="ml-1.5 text-xs text-amber-700">
                        {t('projects.fin.issued.storno')}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                    {formatDate(inv.issueDate)}
                  </td>
                  <td className="px-3 py-2 text-gray-700">{inv.customerName}</td>
                  <td className="px-3 py-2 text-gray-700">
                    {inv.company ? (
                      <span className="inline-flex items-center gap-1">
                        <Building2 size={12} className="text-gray-400" />
                        {inv.company.name}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <IssuedInvoiceStatusBadge status={inv.status} />
                  </td>
                  <td
                    className={`px-3 py-2 text-right font-medium whitespace-nowrap ${inv.totalAmount < 0 ? 'text-amber-700' : 'text-gray-900'}`}
                  >
                    {formatMoney(inv.totalAmount, inv.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// --- Sortable entry table (Invoices-style) ---------------------------------

type SortField = 'date' | 'type' | 'category' | 'amount';
type SortOrder = 'asc' | 'desc';

function SortHeader({
  label,
  field,
  sort,
  order,
  onSort,
  align = 'left',
}: {
  label: string;
  field: SortField;
  sort: SortField;
  order: SortOrder;
  onSort: (field: SortField) => void;
  align?: 'left' | 'right';
}) {
  const active = sort === field;
  return (
    <th
      className={`px-3 py-2 font-medium text-gray-600 ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
      <button
        type="button"
        onClick={() => onSort(field)}
        className="inline-flex items-center gap-1 hover:text-gray-900"
      >
        {label}
        {active && <span aria-hidden>{order === 'asc' ? '▲' : '▼'}</span>}
      </button>
    </th>
  );
}

function EntryTable({ entries, currency }: { entries: FinancialEntry[]; currency: string }) {
  const { t } = useI18n();
  const [sort, setSort] = useState<SortField>('date');
  const [order, setOrder] = useState<SortOrder>('desc');

  function onSort(field: SortField) {
    if (field === sort) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(field);
      setOrder('asc');
    }
  }

  const sorted = useMemo(() => {
    const dir = order === 'asc' ? 1 : -1;
    return [...entries].sort((a, b) => {
      switch (sort) {
        case 'date':
          return (new Date(a.date).getTime() - new Date(b.date).getTime()) * dir;
        case 'type':
          return a.type.localeCompare(b.type) * dir;
        case 'category':
          return a.category.localeCompare(b.category) * dir;
        case 'amount':
          return (a.amount - b.amount) * dir;
        default:
          return 0;
      }
    });
  }, [entries, sort, order]);

  if (entries.length === 0) {
    return <p className="py-8 text-center text-gray-500">{t('projects.fin.noEntries')}</p>;
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <SortHeader
              label={t('projects.fin.col.date')}
              field="date"
              sort={sort}
              order={order}
              onSort={onSort}
            />
            <SortHeader
              label={t('projects.fin.col.type')}
              field="type"
              sort={sort}
              order={order}
              onSort={onSort}
            />
            <SortHeader
              label={t('projects.fin.col.category')}
              field="category"
              sort={sort}
              order={order}
              onSort={onSort}
            />
            <th className="px-3 py-2 text-left font-medium text-gray-600">
              {t('projects.fin.col.description')}
            </th>
            <SortHeader
              label={t('projects.fin.col.amount')}
              field="amount"
              sort={sort}
              order={order}
              onSort={onSort}
              align="right"
            />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {sorted.map((entry) => {
            const isCost = entry.type === 'COST';
            return (
              <tr key={entry.id} className={isCost ? 'row-highlight' : undefined}>
                <td className="px-3 py-2 text-gray-700">{formatDate(entry.date)}</td>
                <td className="px-3 py-2">
                  <span
                    className={`rounded px-2 py-0.5 text-xs font-medium ${
                      isCost ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                    }`}
                  >
                    {isCost ? t('projects.fin.cost') : t('projects.fin.income')}
                  </span>
                </td>
                <td className="px-3 py-2 text-gray-700">{entry.category}</td>
                <td className="px-3 py-2 text-gray-700">{entry.description ?? '—'}</td>
                <td
                  className={`px-3 py-2 text-right font-medium ${
                    isCost ? 'text-red-600' : 'text-green-600'
                  }`}
                >
                  {isCost ? '−' : '+'}
                  {formatMoney(entry.amount, entry.currency || currency)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// --- Main tab --------------------------------------------------------------

export function ProjectFinancialsTab({ project }: { project: ProjectDetail }) {
  const { t } = useI18n();
  const { financials } = project;
  // CLE-432: the backend omits financials for roles without
  // projects.canViewFinancials. This tab is only reachable when that flag is
  // granted (CLE-431 gates the tab), so treat a missing block as "no access".
  if (!financials) return null;
  const { entries, totalRevenue, totalCost, profit, profitMargin, budget } = financials;

  const currency = entries[0]?.currency ?? 'RON';

  // Group entries by year-month for the bar + trend charts.
  const monthly = useMemo(() => {
    const map = new Map<string, { key: string; label: string; revenue: number; cost: number }>();
    for (const e of entries) {
      const key = `${e.year}-${String(e.month).padStart(2, '0')}`;
      const label = `${MONTH_LABELS[e.month - 1] ?? e.month} ${String(e.year).slice(2)}`;
      const row = map.get(key) ?? { key, label, revenue: 0, cost: 0 };
      if (e.type === 'REVENUE') row.revenue += e.amount;
      else row.cost += e.amount;
      map.set(key, row);
    }
    return [...map.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [entries]);

  // Cumulative profit/loss trend.
  const trend = useMemo(() => {
    let running = 0;
    return monthly.map((m) => {
      running += m.revenue - m.cost;
      return { label: m.label, profit: running };
    });
  }, [monthly]);

  // Expense breakdown by category for the pie chart.
  const expenseByCategory = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of entries) {
      if (e.type !== 'COST') continue;
      map.set(e.category, (map.get(e.category) ?? 0) + e.amount);
    }
    return [...map.entries()]
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [entries]);

  // Cash-flow: income vs. outcome per month plus a running balance, for the
  // area / waterfall-style view (CLE-258).
  const cashflow = useMemo(() => {
    let balance = 0;
    return monthly.map((m) => {
      balance += m.revenue - m.cost;
      return { label: m.label, income: m.revenue, outcome: m.cost, balance };
    });
  }, [monthly]);

  // Stacked expense trend: amount per category per month over time so the user
  // sees how the cost distribution shifts (CLE-258).
  const expenseTrend = useMemo(() => {
    const categories = [
      ...new Set(entries.filter((e) => e.type === 'COST').map((e) => e.category)),
    ];
    const byMonth = new Map<string, Record<string, number> & { key: string; label: string }>();
    for (const e of entries) {
      if (e.type !== 'COST') continue;
      const key = `${e.year}-${String(e.month).padStart(2, '0')}`;
      const label = `${MONTH_LABELS[e.month - 1] ?? e.month} ${String(e.year).slice(2)}`;
      const row =
        byMonth.get(key) ??
        ({ key, label, ...Object.fromEntries(categories.map((c) => [c, 0])) } as Record<
          string,
          number
        > & { key: string; label: string });
      row[e.category] = (row[e.category] ?? 0) + e.amount;
      byMonth.set(key, row);
    }
    const rows = [...byMonth.values()].sort((a, b) => a.key.localeCompare(b.key));
    return { categories, rows };
  }, [entries]);

  const budgetUsed = budget && budget > 0 ? (totalCost / budget) * 100 : null;
  const issuedInvoices = financials.issuedInvoices ?? [];
  const invoicedByCompany = financials.invoicedByCompany ?? [];
  const totalInvoiced = financials.totalInvoiced ?? 0;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard
          label={t('projects.fin.revenue')}
          value={formatMoney(totalRevenue, currency)}
          tone="positive"
          icon={<TrendingUp size={16} />}
        />
        <SummaryCard
          label={t('projects.fin.expenses')}
          value={formatMoney(totalCost, currency)}
          hint={
            budgetUsed != null
              ? t('projects.fin.budgetUsed', { percent: budgetUsed.toFixed(0) })
              : undefined
          }
          tone="negative"
          icon={<TrendingDown size={16} />}
        />
        <SummaryCard
          label={t('projects.fin.profitLoss')}
          value={formatMoney(profit, currency)}
          hint={
            profitMargin != null
              ? t('projects.fin.margin', { percent: profitMargin.toFixed(1) })
              : undefined
          }
          tone={profit >= 0 ? 'positive' : 'negative'}
          icon={profit >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
        />
        <SummaryCard
          label={t('projects.fin.budget')}
          value={budget != null ? formatMoney(budget, currency) : '—'}
          hint={
            budget != null
              ? t('projects.fin.budgetRemaining', {
                  amount: formatMoney(budget - totalCost, currency),
                })
              : undefined
          }
          tone="neutral"
          icon={<Wallet size={16} />}
        />
      </div>

      {/* Facturi emise for this project, from every firm */}
      <IssuedInvoicesSection
        invoices={issuedInvoices}
        byCompany={invoicedByCompany}
        totalInvoiced={totalInvoiced}
      />

      {/* Charts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title={t('projects.fin.chart.revenueVsExpenses')}>
          {monthly.length === 0 ? (
            <EmptyChart message={t('projects.fin.chart.noMonthly')} />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <YAxis tickFormatter={formatCompact} tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <Tooltip
                  formatter={(v: number) => formatMoney(v, currency)}
                  contentStyle={CHART_TOOLTIP_STYLE}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar
                  dataKey="revenue"
                  name={t('projects.fin.revenue')}
                  fill="#10b981"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="cost"
                  name={t('projects.fin.expenses')}
                  fill="#ef4444"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title={t('projects.fin.chart.expensesByCategory')}>
          {expenseByCategory.length === 0 ? (
            <EmptyChart message={t('projects.fin.chart.noExpenses')} />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={expenseByCategory}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  innerRadius={50}
                  paddingAngle={2}
                >
                  {expenseByCategory.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v: number) => formatMoney(v, currency)}
                  contentStyle={CHART_TOOLTIP_STYLE}
                  itemStyle={CHART_TOOLTIP_ITEM_STYLE}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title={t('projects.fin.chart.profitTrend')}>
          {trend.length === 0 ? (
            <EmptyChart message={t('projects.fin.chart.noTrend')} />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <YAxis tickFormatter={formatCompact} tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <Tooltip
                  formatter={(v: number) => formatMoney(v, currency)}
                  contentStyle={CHART_TOOLTIP_STYLE}
                />
                <Line
                  type="monotone"
                  dataKey="profit"
                  name={t('projects.fin.chart.cumulativeProfit')}
                  stroke="#6366f1"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title={t('projects.fin.chart.revVsExpTrend')}>
          {monthly.length === 0 ? (
            <EmptyChart message={t('projects.fin.chart.noTrend')} />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthly} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <YAxis tickFormatter={formatCompact} tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <Tooltip
                  formatter={(v: number) => formatMoney(v, currency)}
                  contentStyle={CHART_TOOLTIP_STYLE}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  name={t('projects.fin.revenue')}
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="cost"
                  name={t('projects.fin.expenses')}
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title={t('projects.fin.chart.cashflow')}>
          {cashflow.length === 0 ? (
            <EmptyChart message={t('projects.fin.chart.noCashflow')} />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={cashflow} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="cf-income" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="cf-outcome" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <YAxis tickFormatter={formatCompact} tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <Tooltip
                  formatter={(v: number) => formatMoney(v, currency)}
                  contentStyle={CHART_TOOLTIP_STYLE}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area
                  type="monotone"
                  dataKey="income"
                  name={t('projects.fin.chart.income')}
                  stroke="#10b981"
                  fill="url(#cf-income)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="outcome"
                  name={t('projects.fin.chart.outcome')}
                  stroke="#ef4444"
                  fill="url(#cf-outcome)"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="balance"
                  name={t('projects.fin.chart.balance')}
                  stroke="#6366f1"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title={t('projects.fin.chart.expenseTrend')}>
          {expenseTrend.rows.length === 0 ? (
            <EmptyChart message={t('projects.fin.chart.noExpenseTrend')} />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={expenseTrend.rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <YAxis tickFormatter={formatCompact} tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <Tooltip
                  formatter={(v: number) => formatMoney(v, currency)}
                  contentStyle={CHART_TOOLTIP_STYLE}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {expenseTrend.categories.map((cat, i) => (
                  <Area
                    key={cat}
                    type="monotone"
                    dataKey={cat}
                    name={cat}
                    stackId="exp"
                    stroke={PIE_COLORS[i % PIE_COLORS.length]}
                    fill={PIE_COLORS[i % PIE_COLORS.length]}
                    fillOpacity={0.7}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Entries table (Invoices-style) */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">{t('projects.fin.transactions')}</h3>
          <span className="text-xs text-gray-500">
            {entries.length}{' '}
            {entries.length === 1 ? t('projects.fin.record') : t('projects.fin.records')}
          </span>
        </div>
        <EntryTable entries={entries} currency={currency} />
      </div>

      <div>
        <EditWithAIButton
          label={t('projects.fin.addEntryWithAI')}
          prompt={`Vreau să înregistrez o intrare financiară (venit sau cost) pentru proiectul „${project.name}”. Te rog întreabă-mă despre tip, categorie, sumă și dată, apoi actualizează în Baza de cunoștințe.`}
        />
      </div>
    </div>
  );
}
